<template>
  <div class="events-page">

    <DefaultHeader
      title="Ивенты"
      subtitle="События"
      class="fixed-header"
      :offset="700"
    />


    <!-- =====================================================
         CONTENT
    ====================================================== -->

    <main class="events-content">

      <div class="events-container">

        <!-- =================================================
             HERO
        ================================================== -->

        <section class="events-hero">

          <div class="hero-copy">

            <span class="hero-label">
              События
            </span>

            <h1>
              Ивенты
            </h1>

            <p>
              Встречи, конференции, митапы и события
              от компаний и HR-сообщества.
            </p>

          </div>


          <button
            type="button"
            class="create-event-button"
            :disabled="isCreateEventLoading"
            @click="handleCreateEvent"
          >

            <template v-if="!isCreateEventLoading">

              <span class="create-button-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 5V19"
                    stroke="currentColor"
                    stroke-width="1.9"
                    stroke-linecap="round"
                  />

                  <path
                    d="M5 12H19"
                    stroke="currentColor"
                    stroke-width="1.9"
                    stroke-linecap="round"
                  />
                </svg>
              </span>

              <span>
                Создать ивент
              </span>

            </template>


            <template v-else>

              <span class="button-spinner"></span>

              Проверяем...

            </template>

          </button>

        </section>


        <!-- =================================================
             TOOLBAR
        ================================================== -->

        <section
          v-if="!loading && events.length"
          class="events-toolbar"
        >

          <div class="toolbar-title">

            <span class="toolbar-dot"></span>

            <span>
              Все события
            </span>

            <strong>
              {{ events.length }}
            </strong>

          </div>


          <span class="toolbar-caption">
            Актуальные события
          </span>

        </section>


        <!-- =================================================
             LOADING
        ================================================== -->

        <section
          v-if="loading"
          class="event-cards"
        >

          <EventCardSkeleton
            v-for="index in 4"
            :key="`skeleton-${index}`"
          />

        </section>


        <!-- =================================================
             EMPTY
        ================================================== -->

        <section
          v-else-if="events.length === 0"
          class="empty-state"
        >

          <div class="empty-icon">

            <svg
              viewBox="0 0 24 24"
              fill="none"
            >
              <rect
                x="4"
                y="5"
                width="16"
                height="15"
                rx="3"
                stroke="currentColor"
                stroke-width="1.7"
              />

              <path
                d="M8 3V7"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <path
                d="M16 3V7"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <path
                d="M4 9H20"
                stroke="currentColor"
                stroke-width="1.7"
              />
            </svg>

          </div>


          <h2>
            Пока нет событий
          </h2>

          <p>
            Здесь появятся новые конференции,
            митапы и встречи.
          </p>

        </section>


        <!-- =================================================
             EVENTS
        ================================================== -->

        <section
          v-else
          class="event-cards"
        >

          <EventCard
            v-for="event in events"
            :key="event.id"
            :event="event"
          />

        </section>

      </div>

    </main>


    <MiniFooter
      class="fixed-footer"
    />

  </div>


  <!-- =======================================================
       PAYWALL
  ======================================================== -->

  <teleport to="body">

    <Paywall
      v-if="showPaywall"
      @close="showPaywall = false"
    />

  </teleport>

</template>


<script setup>

import {
  onMounted,
  ref
} from "vue";

import {
  getAuth,
  onAuthStateChanged
} from "firebase/auth";

import {
  useRouter
} from "vue-router";


import Paywall
  from "../views/Paywall.vue";

import EventService
  from "../api/EventService.js";

import EventCard
  from "../components/EventCard.vue";

import DefaultHeader
  from "../components/EventsHeader.vue";

import MiniFooter
  from "../components/MiniFooter.vue";

import EventCardSkeleton
  from "../components/EventCardSkeleton.vue";

import UserService
  from "../api/UserService.js";

import PremiumService
  from "../api/PremiumService.js";


const router =
  useRouter();


const isCreateEventLoading =
  ref(false);

const events =
  ref([]);

const loading =
  ref(true);

const showPaywall =
  ref(false);

const showCompanyDrawer =
  ref(false);


/* ============================================================
   LOAD EVENTS
============================================================ */

onMounted(() => {
  const auth = getAuth();

  onAuthStateChanged(
    auth,
    async user => {
      if (!user) {
        console.warn(
          "❌ Пользователь не авторизован → редирект на hr3.space"
        );

        window.location.replace(
          "https://hr3.space/"
        );

        return;
      }

      try {
        events.value =
          await EventService
            .fetchAllEvents();

        console.log(
          "📅 Events loaded:",
          events.value.length
        );
      } catch (error) {
        console.error(
          "❌ Ошибка загрузки событий:",
          error
        );
      } finally {
        loading.value = false;
      }
    }
  );
});


/* ============================================================
   CREATE EVENT
============================================================ */

const handleCreateEvent =
  async () => {

    if (
      isCreateEventLoading.value
    ) {
      return;
    }


    isCreateEventLoading.value =
      true;


    try {

      console.log(
        "🚀 Создание ивента — старт"
      );


      const premium =
        await PremiumService
          .fetchMySubscription();


      if (
        !premium
        || !premium.is_active
      ) {

        console.log(
          "❌ Нет премиума → открываем Paywall"
        );


        showPaywall.value =
          true;

        return;
      }


      const user =
        await UserService
          .getProfile();


      const currentCompanyId =
        user.current_company?.company_id
        || user.current_company?.companyId
        || null;


      const companyIds =
        Array.isArray(
          user.company_ids
        )
          ? user.company_ids
          : [];


      if (
        companyIds.length > 1
      ) {

        router.push({
          path:
            "/create-event",

          query: {
            "is-multiple-companies":
              "true"
          }
        });

        return;
      }


      if (
        currentCompanyId
      ) {

        localStorage.setItem(
          "event_company_id",
          currentCompanyId
        );


        router.push(
          "/create-event"
        );

        return;
      }


      showCompanyDrawer.value =
        true;


    } catch (error) {

      console.error(
        "❌ Ошибка handleCreateEvent",
        error
      );


      showPaywall.value =
        true;


    } finally {

      isCreateEventLoading.value =
        false;

    }

  };

</script>


<style scoped>

* {
  box-sizing: border-box;
}


/* ============================================================
   PAGE
============================================================ */

.events-page {
  width: 100%;
  height: 100dvh;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  color: #24272d;

  background: #f4f6f9;
}


.fixed-header,
.fixed-footer {
  flex-shrink: 0;
}


.events-content {
  flex: 1;
  min-height: 0;

  overflow-y: auto;
  overflow-x: hidden;

  padding:
    48px
    24px
    80px;

  scrollbar-width: none;

  background:
    radial-gradient(
      circle at 12% 0%,
      rgba(40, 135, 247, 0.075),
      transparent 390px
    ),
    radial-gradient(
      circle at 89% 8%,
      rgba(124, 92, 231, 0.055),
      transparent 350px
    ),
    #f4f6f9;
}


.events-content::-webkit-scrollbar {
  display: none;
}


.events-container {
  width: 100%;
  max-width: 1180px;

  margin: 0 auto;
}


/* ============================================================
   HERO
============================================================ */

.events-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 30px;

  margin-bottom: 32px;
}


.hero-label {
  display: block;

  margin-bottom: 6px;

  color: #989faa;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  letter-spacing: 0.09em;

  text-transform: uppercase;
}


.events-hero h1 {
  margin: 0;

  color: #22262d;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 32px;
  line-height: 1.2;
}


.events-hero p {
  max-width: 520px;

  margin:
    9px
    0
    0;

  color: #7c838e;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 13px;
  line-height: 1.55;
}


/* ============================================================
   CREATE
============================================================ */

.create-event-button {
  min-height: 44px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding:
    0
    17px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #2887f7,
      #486de6
    );

  border: none;
  border-radius: 13px;

  box-shadow:
    0 8px 20px
    rgba(
      40,
      135,
      247,
      0.2
    );

  cursor: pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  transition:
    transform
    0.18s ease,
    box-shadow
    0.18s ease,
    opacity
    0.18s ease;
}


.create-event-button:hover:not(:disabled) {
  transform:
    translateY(-2px);

  box-shadow:
    0 12px 26px
    rgba(
      40,
      135,
      247,
      0.27
    );
}


.create-event-button:disabled {
  opacity: 0.65;
  cursor: default;
}


.create-button-icon {
  width: 23px;
  height: 23px;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    rgba(
      255,
      255,
      255,
      0.14
    );

  border-radius: 7px;
}


.create-button-icon svg {
  width: 14px;
  height: 14px;
}


.button-spinner {
  width: 14px;
  height: 14px;

  border:
    2px solid
    rgba(
      255,
      255,
      255,
      0.35
    );

  border-top-color: #ffffff;

  border-radius: 50%;

  animation:
    spin
    0.75s
    linear
    infinite;
}


/* ============================================================
   TOOLBAR
============================================================ */

.events-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;

  margin-bottom: 18px;

  padding-bottom: 14px;

  border-bottom:
    1px solid
    rgba(
      31,
      44,
      62,
      0.06
    );
}


.toolbar-title {
  display: flex;
  align-items: center;

  gap: 8px;

  color: #555d68;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;
}


.toolbar-dot {
  width: 7px;
  height: 7px;

  border-radius: 50%;

  background: #347fe7;

  box-shadow:
    0 0 0 4px
    rgba(
      52,
      127,
      231,
      0.09
    );
}


.toolbar-title strong {
  min-width: 22px;

  padding:
    3px
    7px;

  color: #727985;

  background:
    rgba(
      110,
      120,
      135,
      0.08
    );

  border-radius: 999px;

  text-align: center;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 8px;
}


.toolbar-caption {
  color: #a0a6af;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
}


/* ============================================================
   GRID
============================================================ */

.event-cards {
  display: grid;

  grid-template-columns:
    repeat(
      2,
      minmax(
        0,
        1fr
      )
    );

  gap: 20px;
}


/* ============================================================
   EMPTY
============================================================ */

.empty-state {
  max-width: 500px;

  margin:
    80px
    auto;

  padding:
    42px
    30px;

  text-align: center;

  background:
    rgba(
      255,
      255,
      255,
      0.9
    );

  border:
    1px solid
    rgba(
      31,
      44,
      62,
      0.065
    );

  border-radius: 22px;

  box-shadow:
    0 14px 38px
    rgba(
      15,
      23,
      42,
      0.05
    );
}


.empty-icon {
  width: 54px;
  height: 54px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin:
    0
    auto
    16px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #2887f7,
      #6659df
    );

  border-radius: 16px;

  box-shadow:
    0 9px 20px
    rgba(
      63,
      105,
      226,
      0.18
    );
}


.empty-icon svg {
  width: 25px;
  height: 25px;
}


.empty-state h2 {
  margin: 0;

  color: #2b3037;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 18px;
}


.empty-state p {
  margin:
    8px
    0
    0;

  color: #858c96;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.events-page {
  color: #f2f2f4;

  background: #18191c;
}


html[data-theme="dark"]
.events-content {
  background:
    radial-gradient(
      circle at 12% 0%,
      rgba(40, 135, 247, 0.065),
      transparent 390px
    ),
    radial-gradient(
      circle at 89% 8%,
      rgba(124, 92, 231, 0.055),
      transparent 350px
    ),
    #18191c;
}


html[data-theme="dark"]
.events-hero h1 {
  color: #f2f3f5;
}


html[data-theme="dark"]
.events-hero p {
  color: #939aa5;
}


html[data-theme="dark"]
.hero-label {
  color: #747b85;
}


html[data-theme="dark"]
.events-toolbar {
  border-bottom-color:
    rgba(
      255,
      255,
      255,
      0.06
    );
}


html[data-theme="dark"]
.toolbar-title {
  color: #c1c5cb;
}


html[data-theme="dark"]
.empty-state {
  background: #232428;

  border-color:
    rgba(
      255,
      255,
      255,
      0.065
    );

  box-shadow:
    0 14px 38px
    rgba(
      0,
      0,
      0,
      0.22
    );
}


html[data-theme="dark"]
.empty-state h2 {
  color: #f0f1f3;
}


/* ============================================================
   TABLET
============================================================ */

@media (
  max-width: 900px
) {

  .events-content {
    padding:
      38px
      18px
      70px;
  }


  .event-cards {
    grid-template-columns:
      1fr;
  }

}


/* ============================================================
   MOBILE
============================================================ */

@media (
  max-width: 600px
) {

  .events-content {
    padding:
      27px
      14px
      55px;
  }


  .events-hero {
    flex-direction: column;

    gap: 18px;

    margin-bottom: 25px;
  }


  .events-hero h1 {
    font-size: 26px;
  }


  .events-hero p {
    font-size: 12px;
  }


  .create-event-button {
    width: 100%;
  }


  .toolbar-caption {
    display: none;
  }


  .event-cards {
    gap: 14px;
  }

}


/* ============================================================
   ANIMATION
============================================================ */

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}


@media (
  prefers-reduced-motion:
  reduce
) {

  .create-event-button {
    transition: none;
  }


  .button-spinner {
    animation: none;
  }

}

</style>